import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import { AuthGuard } from '../../app/AuthGuard';
import { hasAcceptedOffer } from '../../features/carrierCabinet/transportAcceptance';
import type { ScreenId } from '../../app/screenRoutes';
import {
  carrierCabinetApi,
  type ChatDto,
  type MessageDto,
  type TransportDto,
} from '../../features/carrierCabinet/api';
import {
  ChatAppContainer,
  initials,
  UnifiedChatComposer,
  UnifiedChatHeader,
  UnifiedChatSidebar,
  UnifiedMessageList,
} from '../../features/chat/UnifiedChatComponents';
import { mergeMessages, updateChatWithMessage } from '../../features/chat/messageUtils';
import { type ChatRealtimeEvent, useReconnectingWebSocket } from '../../features/chat/realtime';
import { CarrierCabinetLayout, navigateCarrier } from '../../layouts/CarrierCabinet/CarrierCabinetLayout';
import { useAuth } from '../../state/auth';
import { CabinetEmptyState, CabinetLoadState } from '../../ui';
import { useChatStatus } from '../../state/chatStatus';
import { hashNumber, money, PageHeading } from './carrierCabinetShared';
import './carrier-chat.css';

export type CarrierChatScreenId = Extract<ScreenId, 'carrier-chat-list' | 'carrier-chat'>;

const FIGMA_NODES: Record<CarrierChatScreenId, { '1920': string; '1440': string }> = {
  'carrier-chat-list': { '1920': '2607:34757', '1440': '2607:34780' },
  'carrier-chat': { '1920': '2661:36556', '1440': '2662:37465' },
};

function transportStatus(transport?: TransportDto) {
  if (!transport) return '';
  if (transport.status === 'planned') return 'Заявка принята';
  if (transport.status === 'in_progress') return 'В пути';
  if (transport.status === 'delivered' && transport.payment_status === 'customer_marked_paid') {
    return 'Доставлено · оплата подтверждена заказчиком';
  }
  if (transport.status === 'delivered') return 'Доставлено · ждём подтверждение оплаты';
  if (transport.status === 'closed') return 'Перевозка завершена';
  return transport.status;
}

function ChatListView() {
  const auth = useAuth();
  const chatStatus = useChatStatus();
  const [chats, setChats] = useState<ChatDto[]>([]);
  const [query, setQuery] = useState('');
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    const fetchChats = async () => {
      try {
        const items = await carrierCabinetApi.chats();
        if (!active) return;
        setChats(items);
        setState('ready');
      } catch {
        if (!active) return;
        setState('error');
      }
    };
    void fetchChats();
    return () => {
      active = false;
    };
  }, [auth.user?.id, chatStatus.eventVersion]);

  return (
    <div className="carrier-chat-list" data-state={state}>
      <PageHeading title="Сообщения" subtitle="Приватные чаты с заказчиками по вашим сделкам" />
      <CabinetLoadState state={state} />
      {state === 'ready' && !chats.length ? <CabinetEmptyState kind="messages" /> : null}
      {chats.length > 0 ? <ChatAppContainer isCompactList>
        <UnifiedChatSidebar
          chats={chats}
          partnerNameKey="customer_name"
          query={query}
          setQuery={setQuery}
          onSelectChat={(chat) => navigateCarrier('carrier-chat', { chat: chat.id })}
        />
        <div className="customer-chat-placeholder">← Выберите чат</div>
      </ChatAppContainer> : null}
    </div>
  );
}

function ConversationView() {
  const auth = useAuth();
  const chatStatus = useChatStatus();
  const [activeChatId, setActiveChatId] = useState<number | null>(() => hashNumber('chat'));
  const messageViewport = useRef<HTMLDivElement>(null);
  const [chats, setChats] = useState<ChatDto[]>([]);
  const [chat, setChat] = useState<ChatDto | null>(null);
  const [messages, setMessages] = useState<MessageDto[]>([]);
  const [transports, setTransports] = useState<TransportDto[]>([]);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState('');
  const sendInFlight = useRef(false);
  const [state, setState] = useState<'loading' | 'ready' | 'sending' | 'error'>('loading');
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [newMessages, setNewMessages] = useState(0);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [failedAttachment, setFailedAttachment] = useState<File | null>(null);

  useEffect(() => {
    const handleHash = () => {
      const id = hashNumber('chat');
      if (id) setActiveChatId(id);
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const [nextChats, nextTransports] = await Promise.all([
        carrierCabinetApi.chats(),
        carrierCabinetApi.transports(),
      ]);
      const nextChat =
        (activeChatId ? nextChats.find((item) => item.id === activeChatId) : null) ??
        nextChats[0] ??
        null;
      const page = nextChat
        ? await carrierCabinetApi.messagesPage(nextChat.id)
        : { results: [], next_before: null };
      if (!active) return;
      setChats(nextChats);
      setChat(nextChat);
      setMessages(page.results);
      setNextBefore(page.next_before);
      setTransports(nextTransports);
      setState('ready');
      const latest = page.results.at(-1);
      if (nextChat && latest) {
        await carrierCabinetApi.markChatRead(nextChat.id, latest.id);
        if (active) {
          setChats((current) =>
            current.map((item) =>
              item.id === nextChat.id ? { ...item, unread_count: 0 } : item,
            ),
          );
          void chatStatus.refresh().catch(() => {});
        }
      }
    };
    load().catch(() => {
      if (active) setState('error');
    });
    return () => {
      active = false;
    };
  }, [activeChatId]);

  function isNearBottom() {
    const viewport = messageViewport.current;
    return !viewport || viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 80;
  }

  function scrollToBottom() {
    const viewport = messageViewport.current;
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
    setNewMessages(0);
  }

  async function syncLatest() {
    if (!chat) return;
    const [page, nextChats] = await Promise.all([
      carrierCabinetApi.messagesPage(chat.id),
      carrierCabinetApi.chats(),
    ]);
    setMessages((current) => mergeMessages(current, page.results));
    setChats(nextChats);
    const latest = page.results.at(-1);
    if (latest) {
      await carrierCabinetApi.markChatRead(chat.id, latest.id);
      setChats((current) =>
        current.map((item) => (item.id === chat.id ? { ...item, unread_count: 0 } : item)),
      );
      await chatStatus.refresh();
    }
  }

  const socketState = useReconnectingWebSocket<ChatRealtimeEvent>(
    chat ? `/ws/chats/${chat.id}/` : null,
    (event) => {
      if (!chat || event.chat_id !== chat.id) return;
      if (event.event === 'chat.message.created') {
        const message = event.message as MessageDto;
        const follow = isNearBottom();
        setMessages((current) => mergeMessages(current, [message]));
        setChats((current) =>
          updateChatWithMessage(current, chat.id, message, auth.user?.id ?? 0),
        );
        if (message.sender !== auth.user?.id) {
          void carrierCabinetApi
            .markChatRead(chat.id, message.id)
            .then(() => {
              setChats((current) =>
                current.map((item) =>
                  item.id === chat.id ? { ...item, unread_count: 0 } : item,
                ),
              );
              return chatStatus.refresh();
            })
            .catch(() => {});
          if (!follow) setNewMessages((count) => count + 1);
        }
        if (follow) window.requestAnimationFrame(scrollToBottom);
        return;
      }
      if (event.user_id !== auth.user?.id) {
        setMessages((current) =>
          current.map((message) =>
            message.sender === auth.user?.id && message.id <= event.last_read_message_id
              ? { ...message, is_read_by_recipient: true }
              : message,
          ),
        );
      }
    },
    () => {
      void syncLatest().catch(() => {});
    },
  );

  useEffect(() => {
    if (!chat || socketState === 'open') return;
    const timer = window.setInterval(() => {
      void syncLatest().catch(() => {});
    }, 10000);
    return () => window.clearInterval(timer);
  }, [chat?.id, socketState]);

  useEffect(() => {
    if (state === 'ready') window.requestAnimationFrame(scrollToBottom);
  }, [chat?.id]);

  const transport = useMemo(
    () => chat?.phase === 'deal' && chat.deal
      ? transports.find((item) => item.deal === chat.deal && hasAcceptedOffer(item))
      : undefined,
    [chat, transports],
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!chat || !draft.trim() || state === 'sending' || sendInFlight.current) return;
    sendInFlight.current = true;
    const outgoing = draft.trim();
    setState('sending');
    try {
      const message = await carrierCabinetApi.sendMessage(chat.id, outgoing);
      setDraft('');
      setMessages((current) => mergeMessages(current, [message]));
      setChats((current) =>
        updateChatWithMessage(current, chat.id, message, auth.user?.id ?? 0),
      );
      setState('ready');
      window.requestAnimationFrame(scrollToBottom);
    } catch {
      setState('error');
    } finally {
      sendInFlight.current = false;
    }
  }

  async function attach(file?: File) {
    if (!chat || !file) return;
    setAttachmentError('');
    setFailedAttachment(null);
    setUploadProgress(0);
    try {
      const media = await carrierCabinetApi.uploadChatAttachment(file, setUploadProgress);
      const message = await carrierCabinetApi.sendMessage(chat.id, file.name, media);
      setMessages((current) => mergeMessages(current, [message]));
      setChats((current) =>
        updateChatWithMessage(current, chat.id, message, auth.user?.id ?? 0),
      );
      setUploadProgress(null);
      window.requestAnimationFrame(scrollToBottom);
    } catch {
      setUploadProgress(null);
      setFailedAttachment(file);
      setAttachmentError('Не удалось загрузить файл.');
    }
  }

  async function loadOlder() {
    if (!chat || !nextBefore) return;
    setLoadingOlder(true);
    try {
      const page = await carrierCabinetApi.messagesPage(chat.id, nextBefore);
      setMessages((current) => mergeMessages(page.results, current));
      setNextBefore(page.next_before);
    } finally {
      setLoadingOlder(false);
    }
  }

  async function confirmPayment() {
    if (!transport || !hasAcceptedOffer(transport) || transport.payment_status !== 'customer_marked_paid' || state === 'sending') return;
    setState('sending');
    try {
      await carrierCabinetApi.confirmPayment(transport.deal);
      const [nextMessages, nextTransports] = await Promise.all([
        carrierCabinetApi.messages(chat!.id),
        carrierCabinetApi.transports(),
      ]);
      setMessages(nextMessages);
      setTransports(nextTransports);
      setState('ready');
    } catch {
      setState('error');
    }
  }

  return (
    <div className="carrier-chat" data-state={state}>
      <PageHeading title="Сообщения" subtitle="Приватные чаты с заказчиками по вашим сделкам" />
      <ChatAppContainer>
        <UnifiedChatSidebar
          chats={chats}
          partnerNameKey="customer_name"
          query={query}
          selectedId={chat?.id}
          setQuery={setQuery}
          onSelectChat={(item) => {
            setActiveChatId(item.id);
            navigateCarrier('carrier-chat', { chat: item.id });
          }}
        />

        {chat ? (
          <div className="customer-chat__conversation">
            <UnifiedChatHeader
              partnerRole="customer"
              partnerProfileId={chat.customer}
              actions={(
                transport ? (
                  <button
                    className="customer-chat__action-btn--transport"
                    type="button"
                    onClick={() => navigateCarrier('carrier-active-transport', { id: transport.id })}
                  >
                    К перевозке
                  </button>
                ) : null
              )}
              fromCity={chat.from_city}
              partnerName={chat.customer_name}
              requestCode={chat.request_code}
              socketState={socketState}
              statusLabel={transport ? transportStatus(transport) : null}
              toCity={chat.to_city}
            />

            <UnifiedMessageList
              currentUserId={auth.user?.id ?? 0}
              loadingOlder={loadingOlder}
              messages={messages}
              newMessagesCount={newMessages}
              nextBefore={nextBefore}
              viewportRef={messageViewport}
              onLoadOlder={() => void loadOlder()}
              onScrollToBottom={scrollToBottom}
            />

            {transport?.payment_status === 'customer_marked_paid' ? (
              <section className="carrier-chat__confirmation">
                <p>
                  {transport.customer_received_at
                    ? 'Заказчик подтвердил получение питомца'
                    : 'Заказчик отметил оплату'}{' '}
                  · {money(transport.total_price_minor, transport.currency)} переведено напрямую (
                  {money(transport.net_price_minor, transport.currency)} вам +{' '}
                  {money(transport.platform_fee_minor, transport.currency)} комиссия LOVO).
                  Проверьте перевод и подтвердите получение денег.
                </p>
                <button
                  className="carrier-green-button"
                  type="button"
                  disabled={state === 'sending'}
                  onClick={() => void confirmPayment()}
                >
                  ✓ Я получил оплату
                  {transport.customer_received_at ? ' — закрыть сделку' : ''}
                </button>
              </section>
            ) : null}

            <div className="carrier-chat__phase-note">
              {chat.phase === 'negotiation'
                ? 'Приватные переговоры до выбора перевозчика. Стоимость и условия видны только вам и заказчику.'
                : 'Чат выбранной сделки'}
            </div>

            {chat.can_message ? (
              <UnifiedChatComposer
                attachmentError={attachmentError}
                disabled={state === 'sending'}
                draft={draft}
                sending={state === 'sending'}
                uploadProgress={uploadProgress}
                onSend={submit}
                onSetDraft={setDraft}
                onUploadFile={(file) => void attach(file)}
              />
            ) : (
              <div className="carrier-chat__closed">
                Заказчик выбрал другого перевозчика. История переговоров доступна только для
                чтения.
              </div>
            )}
          </div>
        ) : null}
      </ChatAppContainer>
    </div>
  );
}

export function CarrierChatScreen({ screen }: { screen: CarrierChatScreenId }) {
  return (
    <AuthGuard allow={['carrier']}>
      <CarrierCabinetLayout figmaNode={FIGMA_NODES[screen]} screen={screen}>
        {screen === 'carrier-chat-list' ? <ChatListView /> : <ConversationView />}
      </CarrierCabinetLayout>
    </AuthGuard>
  );
}
